import React, { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiUser, FiTrash2, FiEdit2, FiCopy, FiEye } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import Modal from '../components/Modal/Modal';
import SearchSelect from '../components/SearchSelect/SearchSelect';
import { API_BASE_URL } from '../config';
import p from './Pages.module.css';

const USER_TYPES = [
  { id: 1, code: 'admin' }, { id: 2, code: 'customer' },
  { id: 3, code: 'customer_support' }, { id: 4, code: 'employee' },
  { id: 5, code: 'integration' },
];

// NOTE: Defined at module scope on purpose. Defining this inside the `Users`
// component would create a new component type on every render, causing inputs
// to remount and lose focus after each keystroke.
function FormFields({
  n, sn, em, un, pw, tp, intId,
  setN, setSn, setEm, setUn, setPw, setTp, setIntId,
  readonlyUsername,
  integrations, onCreateIntegration,
}) {
  const isIntegration = tp === 'integration';
  const intOptions = integrations.map(i => ({
    value: i.id,
    label: `${i.name || i.code}${i.name ? ` (${i.code})` : ''}${i.active === false ? ' [inactive]' : ''}`,
  }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={p.formRow}>
        <div className={p.formGroup}><label className={p.formLabel}>Name</label><input className={p.formInput} value={n} onChange={e => setN(e.target.value)} /></div>
        <div className={p.formGroup}><label className={p.formLabel}>Surname</label><input className={p.formInput} value={sn} onChange={e => setSn(e.target.value)} /></div>
      </div>
      <div className={p.formGroup}><label className={p.formLabel}>Email</label><input className={p.formInput} type="email" value={em} onChange={e => setEm(e.target.value)} /></div>
      <div className={p.formGroup}><label className={p.formLabel}>Username</label><input className={p.formInput} value={un} readOnly={readonlyUsername} style={readonlyUsername ? { opacity: 0.6, cursor: 'not-allowed' } : {}} onChange={e => setUn(e.target.value)} /></div>
      <div className={p.formGroup}><label className={p.formLabel}>Password</label><input className={p.formInput} type="password" placeholder={readonlyUsername ? 'Leave empty to keep current' : ''} value={pw} onChange={e => setPw(e.target.value)} /></div>
      <div className={p.formGroup}>
        <label className={p.formLabel}>Type</label>
        <select
          className={p.formSelect}
          value={tp}
          onChange={e => {
            const next = e.target.value;
            setTp(next);
            if (next !== 'integration') setIntId('');
          }}
        >
          <option value="">— none —</option>
          {USER_TYPES.map(t => <option key={t.id} value={t.code}>{t.code}</option>)}
        </select>
      </div>
      {isIntegration && (
        <div className={p.formGroup}>
          <label className={p.formLabel}>Integration <span style={{ color: '#d05a5a' }}>*</span></label>
          <SearchSelect
            width="100%"
            options={intOptions}
            value={intId || ''}
            onChange={(v) => setIntId(v)}
            placeholder="Select or type to create…"
            searchPlaceholder="Search integrations…"
            onCreate={onCreateIntegration}
            createLabel="New integration"
          />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Required for integration users. Type a code (e.g. <code>ACME</code>) and press Enter to create a new integration.
          </div>
        </div>
      )}
    </div>
  );
}

function TokenPanel({ token, username }) {
  const [copied, setCopied] = useState(false);
  if (!token) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(token); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div style={{ marginTop: 14, padding: 14, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Permanent integration token</div>
      {username && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>Username: <code>{username}</code></div>}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <input className={p.formInput} value={token} readOnly style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.72rem' }} onFocus={e => e.target.select()} />
        <button className={p.btnOutline} onClick={copy} type="button" title="Copy token">
          <FiCopy /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 8 }}>
        Hand this <code>auth-token</code> to the partner. It does not expire and re-displaying it later returns the same value.
      </div>
    </div>
  );
}

export default function Users() {
  const api = useApi();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [selUser, setSelUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Detail form
  const [name, setName] = useState(''); const [surname, setSurname] = useState('');
  const [email, setEmail] = useState(''); const [username, setUsername] = useState('');
  const [password, setPassword] = useState(''); const [type, setType] = useState('');
  const [integrationId, setIntegrationId] = useState('');

  // Create form
  const [cName, setCName] = useState(''); const [cSurname, setCSurname] = useState('');
  const [cEmail, setCEmail] = useState(''); const [cUsername, setCUsername] = useState('');
  const [cPassword, setCPassword] = useState(''); const [cType, setCType] = useState('');
  const [cIntegrationId, setCIntegrationId] = useState('');

  // Token panels (after-create + reveal-existing)
  const [createdToken, setCreatedToken] = useState(null); // { username, token }
  const [revealedToken, setRevealedToken] = useState(null);

  const loadIntegrations = useCallback(async () => {
    try {
      const r = await api.get('/v1/clasico-integrations/');
      setIntegrations(Array.isArray(r) ? r : (r?.items || []));
    } catch { /* non-fatal */ }
  }, [api]);

  useEffect(() => {
    api.get('/v1/users/').then(d => { if (d) setUsers(d); }).catch(() => toast.error('Failed to load users'));
    loadIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch integrations whenever the create modal is opened so the
  // dropdown always reflects the latest state (e.g. an integration just
  // created in the Settings page).
  useEffect(() => {
    if (showCreate) loadIntegrations();
  }, [showCreate, loadIntegrations]);

  const integrationLabel = useCallback((id) => {
    if (!id) return null;
    const i = integrations.find(x => x.id === id);
    return i ? (i.code || i.name || `#${i.id}`) : `#${id}`;
  }, [integrations]);

  const openUser = (u) => {
    setSelUser(u); setName(u.name ?? ''); setSurname(u.surname ?? '');
    setEmail(u.email ?? ''); setUsername(u.username ?? ''); setPassword(''); setType(u.type ?? '');
    setIntegrationId(u.integrationId ?? '');
    setRevealedToken(null);
  };

  const closeDetail = () => { setSelUser(null); setRevealedToken(null); };
  const closeCreate = () => { setShowCreate(false); setCreatedToken(null); };

  // Build the payload, gating integrationId so it is only sent for type=integration.
  const buildPayload = (n, sn, em, un, pw, tp, intId, { includeBlankPassword }) => {
    const body = { name: n, surname: sn, email: em, username: un, type: tp };
    if (includeBlankPassword || pw) body.password = pw || undefined;
    if (tp === 'integration') body.integrationId = intId ? Number(intId) : null;
    else body.integrationId = null;
    return body;
  };

  // Login as the given user to retrieve their permanent token. Uses a raw
  // fetch so the admin's auth-token header is not sent.
  const fetchUserToken = async (un, pw) => {
    const res = await fetch(`${API_BASE_URL}/v1/login/`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: un, password: pw }),
    });
    if (!res.ok) {
      let msg = `Error ${res.status}`;
      try { const j = await res.json(); msg = j.message || j.error || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const j = await res.json();
    return j.token;
  };

  const onCreateIntegrationFromForm = async (rawCode) => {
    const code = (rawCode || '').trim();
    if (!code) return null;
    try {
      const r = await api.post('/v1/clasico-integrations/', { code, name: code, active: true });
      setIntegrations(prev => [...prev, r]);
      toast.success(`Integration ${r.code} created`);
      return { value: r.id, label: `${r.name || r.code} (${r.code})` };
    } catch (e) { toast.error(e.message); return null; }
  };

  const validateBeforeSubmit = (tp, intId) => {
    if (tp === 'integration' && !intId) {
      toast.error('Pick or create an integration for this user');
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!validateBeforeSubmit(type, integrationId)) return;
    try {
      const body = buildPayload(name, surname, email, username, password, type, integrationId, { includeBlankPassword: false });
      const r = await api.put(`/v1/users/${selUser.id}`, body);
      toast.success('User updated');
      setUsers(prev => prev.map(u => u.id === selUser.id ? r : u));
      closeDetail();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async () => {
    try {
      await api.del(`/v1/users/${selUser.id}`);
      toast.success('User deleted'); setUsers(prev => prev.filter(u => u.id !== selUser.id)); closeDetail();
    } catch (e) { toast.error(e.message); }
  };

  const handleCreate = async () => {
    if (!validateBeforeSubmit(cType, cIntegrationId)) return;
    try {
      const body = buildPayload(cName, cSurname, cEmail, cUsername, cPassword, cType, cIntegrationId, { includeBlankPassword: true });
      const r = await api.post('/v1/users/', body);
      toast.success('User created'); setUsers(prev => [...prev, r]);

      if (cType === 'integration') {
        // Reveal the freshly minted permanent token via a one-shot login.
        try {
          const token = await fetchUserToken(cUsername, cPassword);
          setCreatedToken({ username: cUsername, token });
        } catch (e) {
          toast.error(`User created but token fetch failed: ${e.message}`);
        }
      } else {
        closeCreate();
      }
      // Always clear the form (the token panel still renders from createdToken state).
      setCName(''); setCSurname(''); setCEmail(''); setCUsername(''); setCPassword(''); setCType(''); setCIntegrationId('');
    } catch (e) { toast.error(e.message); }
  };

  const handleRevealToken = async () => {
    if (!password) { toast.error('Enter the user\'s password to reveal the token'); return; }
    try {
      const token = await fetchUserToken(username, password);
      setRevealedToken(token);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className={p.page}>
      <div className={p.pageHeader} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div><h1 className={p.pageTitle}>Users</h1><p className={p.pageSubtitle}>Manage user accounts</p></div>
        <button className={p.btnPrimary} onClick={() => setShowCreate(true)}><FiPlus /> Create User</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {users.length > 0 ? users.map(u => (
          <div key={u.id} className={`${p.card} ${p.cardClickable}`} onClick={() => openUser(u)}
            style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: '0.95rem', flexShrink: 0 }}>
              <FiUser />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--text-primary)' }}>{u.name} {u.surname}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{u.username}</div>
            {u.type && <span className={`${p.badge} ${p.badgeStarted}`}>{u.type}</span>}
            {u.type === 'integration' && u.integrationId && (
              <span className={`${p.badge} ${p.badgeFinished}`}>{integrationLabel(u.integrationId)}</span>
            )}
          </div>
        )) : <div className={p.emptyState}><div className={p.emptyText}>No users found</div></div>}
      </div>

      {/* Detail Modal */}
      <Modal open={!!selUser} onClose={closeDetail} title="User Detail"
        footer={<>
          <button className={p.btnDanger} onClick={handleDelete}><FiTrash2 /> Delete</button>
          <button className={p.btnPrimary} onClick={handleUpdate}><FiEdit2 /> Update</button>
        </>}>
        <FormFields
          n={name} sn={surname} em={email} un={username} pw={password} tp={type} intId={integrationId}
          setN={setName} setSn={setSurname} setEm={setEmail} setUn={setUsername} setPw={setPassword} setTp={setType} setIntId={setIntegrationId}
          readonlyUsername
          integrations={integrations}
          onCreateIntegration={onCreateIntegrationFromForm}
        />
        {selUser?.type === 'integration' && (
          <div style={{ marginTop: 14 }}>
            {!revealedToken ? (
              <button className={p.btnOutline} type="button" onClick={handleRevealToken}>
                <FiEye /> Reveal permanent token
              </button>
            ) : (
              <TokenPanel token={revealedToken} username={username} />
            )}
            {!revealedToken && (
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Enter the user's password above, then click "Reveal" to fetch the token via login.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={closeCreate} title="Create User"
        footer={createdToken ? (
          <button className={p.btnPrimary} onClick={closeCreate}>Done</button>
        ) : (
          <button className={p.btnPrimary} onClick={handleCreate}><FiPlus /> Create</button>
        )}>
        {createdToken ? (
          <TokenPanel token={createdToken.token} username={createdToken.username} />
        ) : (
          <FormFields
            n={cName} sn={cSurname} em={cEmail} un={cUsername} pw={cPassword} tp={cType} intId={cIntegrationId}
            setN={setCName} setSn={setCSurname} setEm={setCEmail} setUn={setCUsername} setPw={setCPassword} setTp={setCType} setIntId={setCIntegrationId}
            readonlyUsername={false}
            integrations={integrations}
            onCreateIntegration={onCreateIntegrationFromForm}
          />
        )}
      </Modal>
    </div>
  );
}

